import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import crmApi, { money, fmtDateTime, CURRENCIES } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

const round3 = n => Math.round((Number(n) || 0) * 1000) / 1000;

function blankProduct() {
  return {
    name: '', category_id: '', unit: 'unit', currency: 'OMR',
    billing_type: 'one_time', track_stock: true, description: '',
    stock_qty: '',
    cost_lines: [{ description: '', amount: '' }],
    price_mode: 'margin', margin_percent: '30', unit_price: '',
  };
}

// sell = cost + (cost × margin/100)
function sellFromMargin(cost, margin) {
  const m = Number(margin) || 0;
  return round3((Number(cost) || 0) + ((Number(cost) || 0) * m / 100));
}

export default function CrmInventory() {
  const { isAdmin } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState(blankProduct());
  const [movementsFor, setMovementsFor] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjust, setAdjust] = useState({ qty_change: '', type: 'restock', note: '' });

  function load(silent) {
    if (!silent) setLoading(true);
    Promise.all([crmApi.get('/products'), crmApi.get('/categories')])
      .then(([p, c]) => { setProducts(p); setCategories(c); })
      .finally(() => { if (!silent) setLoading(false); });
  }
  useEffect(() => { load(); }, []);
  useLiveRefresh(() => load(true));

  // Derived cost/sale/margin for the modal
  const costTotal = round3((form.cost_lines || []).reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const salePrice = form.price_mode === 'margin'
    ? sellFromMargin(costTotal, form.margin_percent)
    : round3(form.unit_price);
  const marginAmount = round3(salePrice - costTotal);
  const marginPctSale = salePrice > 0 ? Math.round(marginAmount / salePrice * 100) : 0;
  const marginPctCost = costTotal > 0 ? Math.round(marginAmount / costTotal * 100) : 0;

  function openNew() { setEditing(null); setForm(blankProduct()); setShow(true); }
  async function doDelete() {
    if (!confirmDel) return;
    try { await crmApi.delete(`/products/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setConfirmDel(null); }
  }
  async function openEdit(p) {
    setEditing(p.id);
    let costLines = [{ description: '', amount: '' }];
    try {
      const costs = await crmApi.get(`/products/${p.id}/costs`);
      if (costs && costs.length) costLines = costs.map(c => ({ description: c.description || '', amount: c.amount }));
    } catch (e) { /* ignore */ }
    setForm({
      name: p.name, category_id: p.category_id || '', unit: p.unit, currency: p.currency || 'OMR',
      billing_type: p.billing_type || 'one_time', track_stock: !!p.track_stock, description: p.description || '',
      stock_qty: '', cost_lines: costLines,
      price_mode: p.price_mode || 'fixed', margin_percent: p.margin_percent != null ? String(p.margin_percent) : '0',
      unit_price: p.unit_price,
    });
    setShow(true);
  }

  function setCostLine(i, patch) { setForm(f => ({ ...f, cost_lines: f.cost_lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) })); }
  function addCostLine() { setForm(f => ({ ...f, cost_lines: [...f.cost_lines, { description: '', amount: '' }] })); }
  function removeCostLine(i) { setForm(f => ({ ...f, cost_lines: f.cost_lines.filter((_, idx) => idx !== i) })); }

  async function save() {
    if (!form.name.trim()) return;
    const payload = {
      name: form.name, category_id: form.category_id, unit: form.unit, currency: form.currency,
      billing_type: form.billing_type, track_stock: form.track_stock, description: form.description,
      cost_lines: (form.cost_lines || []).filter(l => l.description || l.amount).map(l => ({ description: l.description, amount: parseFloat(l.amount) || 0 })),
      price_mode: form.price_mode, margin_percent: parseFloat(form.margin_percent) || 0,
      unit_price: parseFloat(form.unit_price) || 0,
      cost_price: costTotal,
    };
    if (editing) {
      await crmApi.put(`/products/${editing}`, payload);
      // also persist cost lines explicitly
      await crmApi.put(`/products/${editing}/costs`, { cost_lines: payload.cost_lines, price_mode: payload.price_mode, margin_percent: payload.margin_percent, unit_price: payload.unit_price });
    } else {
      payload.stock_qty = parseFloat(form.stock_qty) || 0;
      await crmApi.post('/products', payload);
    }
    setShow(false); load();
  }
  async function openMovements(p) { const full = await crmApi.get(`/products/${p.id}`); setMovementsFor(full); }
  async function doAdjust() {
    const delta = parseFloat(adjust.qty_change);
    if (!delta) return;
    await crmApi.post(`/products/${adjustFor.id}/adjust-stock`, { ...adjust, qty_change: delta });
    setAdjustFor(null); setAdjust({ qty_change: '', type: 'restock', note: '' }); load();
  }


  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '800' }}>Products</h1>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>Each product has a cost build-up, a selling price and a margin. Stock auto-deducts when invoices are sent.</p>
        </div>
        {isAdmin && <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={openNew}>+ New Product</button>}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr 0.9fr 1fr 1.1fr 0.8fr 210px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
          {['Product', 'Category', 'Cost', 'Sell Price', 'Margin', 'Stock', 'Actions'].map(h => (
            <div key={h} style={{ padding: '10px 16px', fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px' }}>{h}</div>
          ))}
        </div>
        {loading ? <div style={{ padding: '20px', color: 'var(--muted)' }}>Loading…</div> :
          products.length === 0 ? <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>No products yet.</div> :
            products.map((p, i) => {
              const mAmt = round3(Number(p.unit_price) - Number(p.cost_price));
              const mPct = Number(p.unit_price) > 0 ? Math.round(mAmt / Number(p.unit_price) * 100) : 0;
              return (
                <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr 0.9fr 1fr 1.1fr 0.8fr 210px', borderBottom: i < products.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center' }}>
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{p.name}</div>
                    <div style={{ fontSize: '11px', color: '#0D9488', fontWeight: '600', fontFamily: 'var(--font-mono)' }}>{p.code || ''}{p.billing_type === 'recurring' ? '  · recurring' : ''}</div>
                  </div>
                  <div style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--muted2)' }}>{p.category_name || '—'}</div>
                  <div style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--muted2)' }}>{money(p.cost_price, p.currency)}</div>
                  <div style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700' }}>{money(p.unit_price, p.currency)}<div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 400 }}>per {p.unit}</div></div>
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: mAmt >= 0 ? '#16A34A' : '#DC2626' }}>{money(mAmt, p.currency)}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{mPct}% margin</div>
                  </div>
                  <div style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '800' }}>{p.track_stock ? `${Number(p.stock_qty)}` : '∞'}<div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 400 }}>{p.track_stock ? p.unit : 'not tracked'}</div></div>
                  <div style={{ padding: '12px 10px', display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm" onClick={() => openMovements(p)} title="Stock history">📜</button>
                    {isAdmin && <button className="btn btn-sm" onClick={() => setAdjustFor(p)} title="Adjust stock">±</button>}
                    {isAdmin && <button className="btn btn-sm" onClick={() => openEdit(p)}>Edit</button>}
                    {isAdmin && <button className="btn btn-sm" onClick={() => setConfirmDel(p)} style={{ color: '#DC2626' }}>Delete</button>}
                  </div>
                </div>
              );
            })}
      </div>

      {/* Create/Edit modal */}
      {show && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShow(false); }}>
          <div className="modal" style={{ width: '600px', maxHeight: '92vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '16px' }}>{editing ? 'Edit Product' : 'New Product'}</h3>
            <div className="form-group"><label className="form-label">Name *</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Starlink Terminal" /></div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Category</label>
                <select className="select" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">— None —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ width: '120px' }}><label className="form-label">Unit</label><input className="input" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="Mbps, unit…" /></div>
              <div className="form-group" style={{ width: '110px' }}><label className="form-label">Currency</label>
                <select className="select" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Billing Type</label>
              <select className="select" value={form.billing_type} onChange={e => setForm({ ...form, billing_type: e.target.value })}>
                <option value="one_time">One-time (sold once)</option>
                <option value="recurring">Recurring (monthly — e.g. Mbps, subscriptions)</option>
              </select>
            </div>

            {/* Cost build-up */}
            <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: '12px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--muted2)' }}>Cost build-up — how this product's cost is made up</div>
                <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={addCostLine}>+ Add cost</button>
              </div>
              {form.cost_lines.map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '7px', alignItems: 'center' }}>
                  <input className="input" style={{ flex: 1 }} value={l.description} onChange={e => setCostLine(i, { description: e.target.value })} placeholder="e.g. Hardware, Software, Staff" />
                  <input className="input" style={{ width: '120px' }} type="number" value={l.amount} onChange={e => setCostLine(i, { amount: e.target.value })} placeholder="0.000" />
                  <button onClick={() => removeCostLine(i)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer', color: '#DC2626', height: '34px', width: '34px' }}>✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '800', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                <span>Total Cost</span><span>{money(costTotal, form.currency)}</span>
              </div>
            </div>

            {/* Pricing */}
            <div className="form-group"><label className="form-label">Selling price determined by</label>
              <select className="select" value={form.price_mode} onChange={e => setForm({ ...form, price_mode: e.target.value })}>
                <option value="margin">Margin % (system calculates the price)</option>
                <option value="fixed">Fixed price (I set the price)</option>
              </select>
            </div>
            {form.price_mode === 'margin' ? (
              <div className="form-group"><label className="form-label">Margin %</label><input className="input" type="number" value={form.margin_percent} onChange={e => setForm({ ...form, margin_percent: e.target.value })} /></div>
            ) : (
              <div className="form-group"><label className="form-label">Selling Price ({form.currency})</label><input className="input" type="number" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} /></div>
            )}

            {/* Live margin summary */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
              <div className="card" style={{ flex: 1, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700 }}>Sell Price</div>
                <div style={{ fontSize: '16px', fontWeight: '800' }}>{money(salePrice, form.currency)}</div>
              </div>
              <div className="card" style={{ flex: 1, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700 }}>Margin</div>
                <div style={{ fontSize: '16px', fontWeight: '800', color: marginAmount >= 0 ? '#16A34A' : '#DC2626' }}>{money(marginAmount, form.currency)}</div>
              </div>
              <div className="card" style={{ flex: 1, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700 }}>Margin %</div>
                <div style={{ fontSize: '16px', fontWeight: '800' }}>{marginPctSale}%</div>
                <div style={{ fontSize: '10px', color: 'var(--muted)' }}>of selling price</div>
              </div>
            </div>

            {!editing && (
              <div className="form-group"><label className="form-label">Opening Stock <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 11 }}>(how many units you have now)</span></label><input className="input" type="number" value={form.stock_qty} onChange={e => setForm({ ...form, stock_qty: e.target.value })} /></div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '14px' }}>
              <input type="checkbox" checked={form.track_stock} onChange={e => setForm({ ...form, track_stock: e.target.checked })} /> Track stock for this product
            </label>
            <div className="form-group"><label className="form-label">Description</label><textarea className="input" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <button className="btn" onClick={() => setShow(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Stock adjust modal */}
      {adjustFor && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setAdjustFor(null); }}>
          <div className="modal" style={{ width: '380px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '4px' }}>Adjust Stock</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '16px' }}>{adjustFor.name} — current: {Number(adjustFor.stock_qty)} {adjustFor.unit}</p>
            <div className="form-group"><label className="form-label">Type</label>
              <select className="select" value={adjust.type} onChange={e => setAdjust({ ...adjust, type: e.target.value })}>
                <option value="restock">Restock (add)</option>
                <option value="adjustment">Adjustment</option>
                <option value="return">Return (add)</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Quantity change <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 11 }}>(negative to reduce)</span></label><input className="input" type="number" value={adjust.qty_change} onChange={e => setAdjust({ ...adjust, qty_change: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Note</label><input className="input" value={adjust.note} onChange={e => setAdjust({ ...adjust, note: e.target.value })} /></div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setAdjustFor(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={doAdjust}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Movements modal */}
      {movementsFor && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setMovementsFor(null); }}>
          <div className="modal" style={{ width: '560px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '800' }}>{movementsFor.name} — Stock History</h3>
              <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setMovementsFor(null)}>✕</button>
            </div>
            {movementsFor.movements.length === 0 && <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No movements recorded.</div>}
            {movementsFor.movements.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'capitalize', width: '90px', color: 'var(--muted2)' }}>{m.movement_type}</span>
                <span style={{ fontSize: '14px', fontWeight: '800', color: Number(m.qty_change) < 0 ? '#DC2626' : '#16A34A', width: '70px' }}>{Number(m.qty_change) > 0 ? '+' : ''}{Number(m.qty_change)}</span>
                <span style={{ fontSize: '12px', color: 'var(--muted)' }}>→ {Number(m.stock_after)}</span>
                <span style={{ flex: 1, fontSize: '11px', color: 'var(--muted2)' }}>{m.note || ''}</span>
                <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{fmtDateTime(m.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {confirmDel && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setConfirmDel(null); }}>
          <div className="modal" style={{ width: '380px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '8px' }}>Delete product?</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}>
              <strong>{confirmDel.name}</strong> will be removed from the product list. Past documents that already use it are not affected.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="btn" style={{ background: '#DC2626', color: '#fff', borderColor: '#DC2626' }} onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
